import { useState, useEffect, useRef, useMemo } from "react";
import { fetchPayoutPolicy, fetchVenuesList } from "./payoutMobileApi.js";
import { ensureVenuePolicy, resolveTierForAmount, getSignerSlots } from "./payoutPolicyUtils.js";

/** role_key → display label. Custom group labels live in the staff_roles
 *  lookup; this humanises the key as a safe fallback (patron → Player). */
const humanizeRole = (key) => {
  if (!key) return "—";
  if (key === "patron") return "Player";
  return String(key).split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};
import {
  Shield, Camera, ChevronLeft, ChevronRight, Send, CheckCircle,
  AlertTriangle, User, Users, CreditCard, ScanLine, FileCheck, Eye, EyeOff,
  MapPin, Clock, X, ChevronDown, Phone, ArrowRight,
  Search, Plus, Home, Bell, Settings, Menu, XCircle, Loader,
  FileWarning, UserCheck, Check, Info, AlertCircle, Lock, Upload, MessageCircle
} from "lucide-react";

// ─── Brand Tokens (matching SVCP web) ───────────────────────────
const brand = {
  primary: "#0a5169",
  primaryLight: "#0d6d8a",
  primaryDark: "#07384a",
  primaryMuted: "#e8f2f6",
  accent: "#fde300",
  accentHover: "#e6cf00",
  accentMuted: "#fef9cc",
  surface: "#f8fafb",
  surfaceWhite: "#ffffff",
  textPrimary: "#0f172a",
  textSecondary: "#475569",
  textMuted: "#94a3b8",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  success: "#059669",
  successBg: "#ecfdf5",
  error: "#dc2626",
  errorBg: "#fef2f2",
  warning: "#d97706",
  warningBg: "#fffbeb",
};

// ─── Styles ─────────────────────────────────────────────────────
const styles = {
  phoneFrame: {
    width: 390,
    height: 844,
    borderRadius: 44,
    background: "#000",
    padding: 12,
    boxShadow: "0 50px 100px -20px rgba(10,81,105,0.25), 0 30px 60px -30px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(255,255,255,0.1)",
    position: "relative",
  },
  phoneInner: {
    width: "100%",
    height: "100%",
    borderRadius: 34,
    overflow: "hidden",
    background: brand.surface,
    position: "relative",
    display: "flex",
    flexDirection: "column",
  },
  statusBar: {
    height: 50,
    background: brand.primary,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    padding: "0 28px 6px",
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    flexShrink: 0,
  },
  header: {
    background: brand.primary,
    padding: "8px 20px 18px",
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "#fff",
    letterSpacing: "-0.02em",
  },
  headerSub: {
    fontSize: 12,
    color: "rgba(255,255,255,0.65)",
    marginTop: 2,
  },
  body: {
    flex: 1,
    overflow: "auto",
    padding: "16px 16px 100px",
  },
  tabBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 80,
    background: "#fff",
    borderTop: `1px solid ${brand.border}`,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-around",
    paddingTop: 10,
    borderRadius: "0 0 34px 34px",
  },
  tabItem: (active) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 3,
    cursor: "pointer",
    color: active ? brand.primary : brand.textMuted,
    fontSize: 10,
    fontWeight: active ? 700 : 500,
    transition: "all 0.2s",
  }),
  card: {
    background: "#fff",
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    border: `1px solid ${brand.border}`,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: brand.textPrimary,
    marginBottom: 4,
  },
  cardSub: {
    fontSize: 12,
    color: brand.textSecondary,
    lineHeight: 1.5,
  },
  btn: (variant = "primary") => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: variant === "large" ? "16px 24px" : "12px 20px",
    borderRadius: 12,
    border: "none",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: variant === "large" ? 16 : 14,
    transition: "all 0.2s",
    width: "100%",
    ...(variant === "primary" || variant === "large"
      ? { background: brand.accent, color: brand.primaryDark }
      : variant === "secondary"
      ? { background: brand.primaryMuted, color: brand.primary }
      : variant === "outline"
      ? { background: "transparent", color: brand.primary, border: `1.5px solid ${brand.primary}` }
      : variant === "ghost"
      ? { background: "transparent", color: brand.textSecondary }
      : variant === "danger"
      ? { background: brand.errorBg, color: brand.error }
      : {}),
  }),
  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 10,
    border: `1.5px solid ${brand.border}`,
    fontSize: 14,
    color: brand.textPrimary,
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: brand.textSecondary,
    marginBottom: 6,
    display: "block",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  badge: (color) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    background: color === "success" ? brand.successBg : color === "error" ? brand.errorBg : color === "warning" ? brand.warningBg : brand.primaryMuted,
    color: color === "success" ? brand.success : color === "error" ? brand.error : color === "warning" ? brand.warning : brand.primary,
  }),
  stepIndicator: (active, completed) => ({
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
    background: completed ? brand.success : active ? brand.primary : brand.border,
    color: completed || active ? "#fff" : brand.textMuted,
    transition: "all 0.3s",
  }),
  stepLine: (completed) => ({
    width: 2,
    height: 24,
    background: completed ? brand.success : brand.border,
    margin: "0 auto",
    transition: "all 0.3s",
  }),
  progressBar: {
    height: 4,
    background: brand.border,
    borderRadius: 2,
    overflow: "hidden",
    marginTop: 12,
  },
  progressFill: (pct) => ({
    height: "100%",
    width: `${pct}%`,
    background: `linear-gradient(90deg, ${brand.primary}, ${brand.primaryLight})`,
    borderRadius: 2,
    transition: "width 0.5s ease",
  }),
};

// ─── Small Components ───────────────────────────────────────────
const StatusBar = () => (
  <div style={styles.statusBar}>
    <span>9:41</span>
    <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
      <div style={{ width: 16, height: 10, borderRadius: 2, border: "1.5px solid #fff", position: "relative" }}>
        <div style={{ position: "absolute", top: 2, left: 2, right: 2, bottom: 2, background: "#fff", borderRadius: 1 }} />
      </div>
      <span style={{ fontSize: 11 }}>5G</span>
    </div>
  </div>
);

const Header = ({ title, subtitle, onBack, rightAction }) => (
  <div style={styles.header}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {onBack && (
          <div onClick={onBack} style={{ cursor: "pointer", padding: 4, margin: -4 }}>
            <ChevronLeft size={22} color="#fff" />
          </div>
        )}
        <div>
          <div style={styles.headerTitle}>{title}</div>
          {subtitle && <div style={styles.headerSub}>{subtitle}</div>}
        </div>
      </div>
      {rightAction && rightAction}
    </div>
  </div>
);

const StepProgress = ({ steps, current }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 0, padding: "0 8px", marginBottom: 16 }}>
    {steps.map((s, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : 0 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 40 }}>
          <div style={styles.stepIndicator(i === current, i < current)}>
            {i < current ? <Check size={15} /> : i + 1}
          </div>
          <span style={{ fontSize: 9, fontWeight: 600, color: i <= current ? brand.primary : brand.textMuted, textAlign: "center", maxWidth: 56, lineHeight: 1.2 }}>{s}</span>
        </div>
        {i < steps.length - 1 && (
          <div style={{ flex: 1, height: 2, background: i < current ? brand.success : brand.border, margin: "0 4px", marginBottom: 18, borderRadius: 1, transition: "background 0.3s" }} />
        )}
      </div>
    ))}
  </div>
);

const BottomTabBar = ({ activeTab, onTabChange }) => {
  const tabs = [
    { id: "home", label: "Home", icon: Home },
    { id: "payout", label: "Payout", icon: CreditCard },
    { id: "incident", label: "Incident", icon: AlertTriangle },
    { id: "alerts", label: "Alerts", icon: Bell },
  ];
  return (
    <div style={styles.tabBar}>
      {tabs.map((t) => (
        <div key={t.id} style={styles.tabItem(activeTab === t.id)} onClick={() => onTabChange(t.id)}>
          <div style={{ position: "relative" }}>
            <t.icon size={22} strokeWidth={activeTab === t.id ? 2.5 : 1.8} />
            {t.id === "alerts" && (
              <div style={{ position: "absolute", top: -4, right: -6, width: 14, height: 14, borderRadius: 7, background: brand.error, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800, color: "#fff" }}>3</div>
            )}
          </div>
          <span>{t.label}</span>
          {activeTab === t.id && <div style={{ width: 20, height: 3, borderRadius: 2, background: brand.primary, marginTop: -1 }} />}
        </div>
      ))}
    </div>
  );
};

// ─── LOGIN SCREEN (inside phone frame) ────────────────────────────
const LoginScreen = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (username.trim() && password) {
        await new Promise((r) => setTimeout(r, 600));
        onLogin?.({ username: username.trim() });
      } else {
        setError("Please enter username and password");
      }
    } catch {
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: brand.primary,
      padding: 24,
      minHeight: 0,
      overflow: "auto",
      boxSizing: "border-box",
    }}>
      <div style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", minWidth: 0, marginTop: -48 }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <img
            src={new URL("./assets/images/paysafego.png", import.meta.url).href}
            alt="PaySafeGO"
            style={{ height: 48, width: "auto", display: "block", marginLeft: "auto", marginRight: "auto" }}
          />
        </div>
        {error && (
          <div style={{ background: "#fef2f2", border: `1px solid ${brand.error}`, color: brand.error, fontSize: 12, padding: "8px 12px", borderRadius: 8, marginBottom: 12 }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ width: "100%", boxSizing: "border-box" }}>
            <label style={{ ...styles.label, marginBottom: 4, color: "rgba(255,255,255,0.8)" }}>Username</label>
            <div style={{ position: "relative", width: "100%" }}>
              <User size={16} color={brand.textMuted} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                style={{ ...styles.input, paddingLeft: 36, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
              />
            </div>
          </div>
          <div style={{ width: "100%", boxSizing: "border-box" }}>
            <label style={{ ...styles.label, marginBottom: 4, color: "rgba(255,255,255,0.8)" }}>Password</label>
            <div style={{ position: "relative", width: "100%" }}>
              <Lock size={16} color={brand.textMuted} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                style={{ ...styles.input, paddingLeft: 36, paddingRight: 40, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: brand.textMuted }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} style={styles.btn("primary")}>
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── HOME SCREEN ─────────────────────────────────────────────────
const HomeScreen = ({ onNav }) => {
  const greeting = "Good morning";
  return (
    <>
      <div style={{ ...styles.header, paddingBottom: 24, background: `linear-gradient(160deg, ${brand.primaryDark} 0%, ${brand.primary} 50%, ${brand.primaryLight} 100%)` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)" }}>
              <Shield size={22} color={brand.accent} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>{greeting}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#fff" }}>TwinStars Diggers</div>
            </div>
          </div>
        </div>
        {/* Quick Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {[
            { label: "Payouts", value: "24" },
            { label: "Incidents submitted", value: "5" },
          ].map((s, i) => (
            <div key={i} style={{ background: "rgba(255,255,255,0.1)", borderRadius: 12, padding: "12px 10px", backdropFilter: "blur(10px)" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>{s.value}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", fontWeight: 500, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={styles.body}>
        {/* Quick Actions */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: brand.textPrimary, marginBottom: 10 }}>Quick Actions</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div onClick={() => onNav("payout")} style={{ ...styles.card, marginBottom: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, padding: 14, border: `1.5px solid ${brand.primaryMuted}`, transition: "all 0.2s" }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: `linear-gradient(135deg, ${brand.primary}, ${brand.primaryLight})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <CreditCard size={20} color={brand.accent} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: brand.textPrimary }}>Process Payout</div>
                <div style={{ fontSize: 11, color: brand.textMuted }}>Capture slip & verify identity</div>
              </div>
            </div>
            <div onClick={() => onNav("incident")} style={{ ...styles.card, marginBottom: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, padding: 14, border: `1.5px solid ${brand.primaryMuted}`, transition: "all 0.2s" }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: `linear-gradient(135deg, ${brand.warning}, #f59e0b)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <AlertTriangle size={20} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: brand.textPrimary }}>Incident</div>
                <div style={{ fontSize: 11, color: brand.textMuted }}>Report activity</div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div style={{ fontSize: 13, fontWeight: 700, color: brand.textPrimary, marginBottom: 10 }}>Recent Activity</div>
        {[
          { icon: CheckCircle, iconColor: brand.success, title: "Payout processed — David R.", sub: "Payout $7,200", time: "25 min ago" },
          { icon: AlertTriangle, iconColor: brand.warning, title: "Incident Filed — EGM 042", sub: "Suspected third-party collection", time: "1h ago" },
          { icon: FileCheck, iconColor: brand.primary, title: "Payout processed — Lisa M.", sub: "Payout $5,800", time: "2h ago" },
          { icon: Bell, iconColor: brand.error, title: "Action required", sub: "Follow-up needed", time: "3h ago" },
        ].map((a, i) => (
          <div key={i} style={{ ...styles.card, display: "flex", alignItems: "flex-start", gap: 12, padding: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `${a.iconColor}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <a.icon size={18} color={a.iconColor} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: brand.textPrimary }}>{a.title}</div>
              <div style={{ fontSize: 11, color: brand.textMuted, marginTop: 2 }}>{a.sub}</div>
            </div>
            <span style={{ fontSize: 10, color: brand.textMuted, flexShrink: 0, marginTop: 2 }}>{a.time}</span>
          </div>
        ))}
      </div>
    </>
  );
};

// ─── SIGNATURE PAD (finger/mouse draw) ──────────────────────────
const SignaturePad = ({ title, onDone, onCancel }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const lastPos = useRef(null);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const draw = (pos) => {
    const canvas = canvasRef.current;
    if (!canvas || !lastPos.current) return;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = brand.textPrimary;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
    lastPos.current = pos;
  };

  const startDraw = (e) => {
    e.preventDefault();
    const pos = getPos(e);
    if (pos) {
      lastPos.current = pos;
      setIsDrawing(true);
    }
  };

  const moveDraw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const pos = getPos(e);
    if (pos) draw(pos);
  };

  const endDraw = () => setIsDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const capture = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onDone?.(canvas.toDataURL("image/png"));
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const w = parent.clientWidth || 300;
    const h = parent.clientHeight || 150;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
  }, []);

  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 100, background: brand.surface, display: "flex", flexDirection: "column", fontFamily: "inherit" }}>
      <div style={{ padding: "12px 16px", background: brand.primary, color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <button type="button" onClick={onCancel} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: 4 }}>
          <X size={20} />
        </button>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{title}</span>
        <div style={{ width: 28 }} />
      </div>
      <div style={{ flex: 1, padding: 12, display: "flex", flexDirection: "column", minHeight: 0, overflow: "auto" }}>
        <div style={{ fontSize: 11, color: brand.textMuted, marginBottom: 6 }}>Sign with your finger below</div>
        <div
          style={{
            width: "100%",
            height: 140,
            border: `2px solid ${brand.border}`,
            borderRadius: 12,
            background: "#fff",
            overflow: "hidden",
            touchAction: "none",
            position: "relative",
          }}
        >
          <canvas
            ref={canvasRef}
            onMouseDown={startDraw}
            onMouseMove={moveDraw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={moveDraw}
            onTouchEnd={endDraw}
            style={{ width: "100%", height: "100%", display: "block", cursor: "crosshair" }}
          />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button type="button" style={styles.btn("secondary")} onClick={clear}>Clear</button>
          <button type="button" style={styles.btn("primary")} onClick={capture}>
            <CheckCircle size={16} />
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

/** DVS (Document Verification Service) — privacy consent before Identity Verification Services check */
const DVS_PRIVACY_CONSENT_PARAS = [
  "By continuing, you expressly consent to the collection, use, and disclosure of your personal information for identity verification through the Australian Government's Identity Verification Services. Your information will only be used to confirm your identity and it will be handled in accordance with the Privacy Act 1988 (Cth) and the Identity Verification Services Act 2023 (Cth).",
  "If you do not give this consent, we will be unable to verify your identity and you may be unable to access the relevant product or service.",
  "By providing your personal details, you confirm that you have read and understood this consent and agree to proceed with identity verification using the DVS.",
];

const DvsPrivacyConsentModal = ({ onAgree, onDecline }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-labelledby="dvs-consent-title"
    style={{
      position: "absolute",
      inset: 0,
      zIndex: 200,
      background: "rgba(15, 23, 42, 0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
      backdropFilter: "blur(4px)",
    }}
    onClick={(e) => e.target === e.currentTarget && onDecline?.()}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        maxWidth: 340,
        maxHeight: "min(78vh, 620px)",
        background: "#ffffff",
        borderRadius: 16,
        boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        border: `1px solid ${brand.border}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "18px 18px 12px", borderBottom: `1px solid ${brand.borderLight}` }}>
        <h2 id="dvs-consent-title" style={{ fontSize: 17, fontWeight: 800, color: brand.textPrimary, lineHeight: 1.3, margin: 0 }}>
          Privacy consent statement
        </h2>
      </div>
      <div
        style={{
          padding: "14px 18px",
          overflowY: "auto",
          flex: 1,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {DVS_PRIVACY_CONSENT_PARAS.map((text, i) => (
          <p
            key={i}
            style={{
              fontSize: 13,
              lineHeight: 1.55,
              color: brand.textSecondary,
              margin: 0,
              marginBottom: i < DVS_PRIVACY_CONSENT_PARAS.length - 1 ? 16 : 0,
            }}
          >
            {text}
          </p>
        ))}
      </div>
      <div style={{ padding: "12px 18px 18px", display: "flex", flexDirection: "column", gap: 10, borderTop: `1px solid ${brand.borderLight}`, background: "#fafafa" }}>
        <button type="button" style={styles.btn("primary")} onClick={onAgree}>
          Agree & continue
          <ArrowRight size={16} />
        </button>
        <button type="button" style={styles.btn("outline")} onClick={onDecline}>
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// ─── MJ1: PAYOUT KYC JOURNEY ────────────────────────────────────
const MJ1Screen = () => {
  const [step, setStep] = useState(0); // 0=slip, 1=mode, 2=scan, 3=ocr, 4=dvs, 5=done, 6=payment+signature
  const [kycMode, setKycMode] = useState(null);
  const [kycSkipped, setKycSkipped] = useState(false);
  const [payoutPolicy, setPayoutPolicy] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [slip, setSlip] = useState({
    serialNo: "691674",
    houseNo: "303",
    playerFirst: "David",
    playerLast: "Morrison",
    memberNo: "3022",
    amount: "7200",
    egm: "EGM-042",
    jackpotType: "Cancel Credit",
    occupation: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  /** Captured signature image per slot key (see getSignerSlots — cashier / supervisor / … per rule) */
  const [signatureImages, setSignatureImages] = useState({});
  const [signingFor, setSigningFor] = useState(null); // { key, label } | null when showing pad
  const [scanning, setScanning] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [dvsStatus, setDvsStatus] = useState(null);
  const [docFrontCaptured, setDocFrontCaptured] = useState(false);
  const [docBackCaptured, setDocBackCaptured] = useState(null); // null = not yet, true = captured, false = skipped
  const [showBackScanUI, setShowBackScanUI] = useState(false); // true = showing camera for back scan
  const [selfKycPhase, setSelfKycPhase] = useState(null); // 'sent' | 'complete' — for self KYC waiting
  const [selfKycMobile, setSelfKycMobile] = useState("");
  const [nswDigitalDataReceived, setNswDigitalDataReceived] = useState(false); // true when patron scans & shares
  const [showDvsConsent, setShowDvsConsent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPolicyLoading(true);
        const envVid = import.meta.env?.VITE_VENUE_ID;
        let vid = envVid;
        if (!vid) {
          const venues = await fetchVenuesList();
          const first = venues[0];
          vid = first?.venue_id ?? first?.id ?? first?.code;
        }
        if (!vid) throw new Error("No venue — set VITE_VENUE_ID or add a venue in the admin API");
        const data = await fetchPayoutPolicy(vid);
        if (!cancelled) setPayoutPolicy(data.payoutPolicy || {});
      } catch {
        if (!cancelled) {
          setPayoutPolicy(ensureVenuePolicy({ payoutPolicy: {} }).payoutPolicy);
        }
      } finally {
        if (!cancelled) setPolicyLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const amount = slip.amount;
  const amountNum = parseFloat(amount) || 0;
  const activeTier = useMemo(() => resolveTierForAmount(amountNum, payoutPolicy), [amountNum, payoutPolicy]);
  const payTier = activeTier; // tier for slip amount (used on payment step)
  const signerSlots = useMemo(
    () => getSignerSlots(payTier?.policy).map((s) => ({ ...s, label: humanizeRole(s.roleId) })),
    [payTier?.policy]
  );
  const cashDispensed = payTier?.policy?.cashDispensed || "CRT_OR_CASH";

  useEffect(() => {
    if (step !== 6 || !payTier?.policy) return;
    const cd = payTier.policy.cashDispensed || "CRT_OR_CASH";
    if (cd === "NO_CASH" && paymentMethod === "Cash") setPaymentMethod("EFT");
    if (cd === "CASH_ONLY" && (paymentMethod === "EFT" || paymentMethod === "Cheque")) setPaymentMethod("Cash");
  }, [step, payTier?.tierId, payTier?.policy?.cashDispensed, paymentMethod]);

  const progressSteps = kycSkipped ? ["Slip", "Pay"] : ["Slip", "Mode", "Scan ID", "Review", "DVS", "Done", "Pay"];
  const progressCurrent = kycSkipped ? (step === 0 ? 0 : 1) : step;

  const handleSlipContinue = () => {
    const tier = resolveTierForAmount(amountNum, payoutPolicy);
    if (!tier || !tier.policy?.enabled) {
      window.alert("This amount does not match an enabled payout tier for this venue. Adjust the amount or enable tiers in Payout Rules.");
      return;
    }
    if (tier.policy.occupationRequired && !String(slip.occupation || "").trim()) {
      window.alert("Occupation is required for this payout tier.");
      return;
    }
    if (!tier.policy.kycRequired) {
      setKycSkipped(true);
      setKycMode(null);
      setStep(6);
      return;
    }
    setKycSkipped(false);
    setStep(1);
  };

  const handleScan = (side) => {
    setScanning(true);
    setTimeout(() => {
      setScanning(false);
      if (side === "front") {
        setDocFrontCaptured(true);
      } else {
        setDocBackCaptured(true);
        setStep(3);
      }
    }, 2000);
  };

  const handleSkipBack = () => {
    setDocBackCaptured(false);
    setStep(3);
  };

  const handleDVS = () => {
    setStep(4);
    setVerifying(true);
    setTimeout(() => { setDvsStatus("clear"); setVerifying(false); setStep(5); }, 2200);
  };

  const handleSelfKYC = () => {
    setKycMode("self");
    setStep(2);
  };

  const handleNswDigital = () => {
    setKycMode("nsw_digital");
    setStep(2);
  };

  const handleSendSelfKycLink = () => {
    const raw = String(selfKycMobile || "").trim();
    const digits = raw.replace(/\D/g, "");
    if (digits.length < 8) {
      window.alert("Enter a valid mobile number (e.g. +61 412 345 678 or 0412345678).");
      return;
    }
    setSelfKycPhase("sent");
    setStep(5);
  };

  // Simulate backend notifying when patron completes self KYC
  useEffect(() => {
    if (kycMode === "self" && selfKycPhase === "sent") {
      const t = setTimeout(() => setSelfKycPhase("complete"), 8000);
      return () => clearTimeout(t);
    }
  }, [kycMode, selfKycPhase]);

  const resetJourney = () => {
    setStep(0); setKycMode(null); setKycSkipped(false);
    setSlip((s) => ({ ...s, amount: "7200", occupation: "" }));
    setScanning(false); setVerifying(false); setDvsStatus(null);
    setDocFrontCaptured(false); setDocBackCaptured(null); setShowBackScanUI(false);
    setSelfKycPhase(null); setNswDigitalDataReceived(false);
    setPaymentMethod("Cash"); setSignatureImages({}); setSigningFor(null);
    setSelfKycMobile("");
    setShowDvsConsent(false);
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, position: "relative" }}>
      <Header
        title="Process Payout"
        subtitle={
          step === 0 ? "Enter payout details" : step === 1 ? "Select verification method" : step === 6 ? "Payment & signature" : `Step ${step} of 6`
        }
        onBack={
          step > 0
            ? () => {
                if (step === 6 && kycSkipped) {
                  setStep(0);
                  return;
                }
                if (step === 1 && kycMode) {
                  setStep(0);
                  setKycMode(null);
                } else if (step > 0) setStep(step - 1);
              }
            : null
        }
      />
      <div style={styles.body}>
        {policyLoading && (
          <div style={{ ...styles.card, textAlign: "center", padding: 20, marginBottom: 12 }}>
            <Loader size={24} color={brand.primary} style={{ animation: "spin 1.5s linear infinite", marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: brand.textSecondary }}>Loading venue payout rules…</div>
          </div>
        )}
        <StepProgress steps={progressSteps} current={progressCurrent} />

        {/* Step 0: Payout slip (amount first — drives tier rules) */}
        {step === 0 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Payout Slip</div>
              {activeTier && (
                <div style={{ fontSize: 11, color: brand.textMuted, marginBottom: 10, padding: "8px 10px", background: brand.primaryMuted, borderRadius: 8 }}>
                  Tier <strong style={{ color: brand.primary }}>{activeTier.tierId}</strong>
                  {activeTier.policy.kycRequired ? " · Identity verification required" : " · No identity verification required"}{" "}
                  · {String(activeTier.policy.cashDispensed || "").replaceAll("_", " ")}
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                <div>
                  <label style={styles.label}>Serial #</label>
                  <input style={styles.input} value={slip.serialNo} onChange={(e) => setSlip((s) => ({ ...s, serialNo: e.target.value }))} />
                </div>
                <div>
                  <label style={styles.label}>House #</label>
                  <input style={styles.input} value={slip.houseNo} onChange={(e) => setSlip((s) => ({ ...s, houseNo: e.target.value }))} />
                </div>
                <div>
                  <label style={styles.label}>Player First Name</label>
                  <input style={styles.input} value={slip.playerFirst} onChange={(e) => setSlip((s) => ({ ...s, playerFirst: e.target.value }))} />
                </div>
                <div>
                  <label style={styles.label}>Player Last Name</label>
                  <input style={styles.input} value={slip.playerLast} onChange={(e) => setSlip((s) => ({ ...s, playerLast: e.target.value }))} />
                </div>
                <div>
                  <label style={styles.label}>Member #</label>
                  <input style={styles.input} value={slip.memberNo} onChange={(e) => setSlip((s) => ({ ...s, memberNo: e.target.value }))} />
                </div>
                <div>
                  <label style={styles.label}>EGM</label>
                  <input style={styles.input} value={slip.egm} onChange={(e) => setSlip((s) => ({ ...s, egm: e.target.value }))} />
                </div>
                <div>
                  <label style={styles.label}>Jackpot Type</label>
                  <input style={styles.input} value={slip.jackpotType} onChange={(e) => setSlip((s) => ({ ...s, jackpotType: e.target.value }))} />
                </div>
                <div>
                  <label style={styles.label}>Payout Amount ($)</label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14, fontWeight: 700, color: brand.primary }}>$</span>
                    <input style={{ ...styles.input, paddingLeft: 28, fontSize: 16, fontWeight: 700 }} value={slip.amount} onChange={(e) => setSlip((s) => ({ ...s, amount: e.target.value }))} />
                  </div>
                </div>
              </div>
              {activeTier?.policy?.occupationRequired && (
                <div style={{ marginTop: 12 }}>
                  <label style={styles.label}>Occupation (required)</label>
                  <input style={styles.input} value={slip.occupation} onChange={(e) => setSlip((s) => ({ ...s, occupation: e.target.value }))} placeholder="e.g. Teacher" />
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={styles.btn("primary")} disabled={policyLoading} onClick={handleSlipContinue}>
                Continue
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Choose verification mode (only when policy requires KYC) */}
        {step === 1 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div onClick={() => { setKycMode("assisted"); setStep(2); }} style={{ ...styles.card, cursor: "pointer", border: `1.5px solid ${brand.primary}20`, padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: `linear-gradient(135deg, ${brand.primary}, ${brand.primaryLight})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ScanLine size={24} color={brand.accent} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: brand.textPrimary }}>Assisted KYC</div>
                  <div style={{ fontSize: 12, color: brand.textSecondary, marginTop: 3, lineHeight: 1.4 }}>
                    Cashier scans ID, verifies patron identity and runs DVS check
                  </div>
                </div>
                <ChevronRight size={20} color={brand.textMuted} />
              </div>
            </div>

            <div onClick={handleSelfKYC} style={{ ...styles.card, cursor: "pointer", border: `1.5px solid ${brand.primary}20`, padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: `linear-gradient(135deg, ${brand.primaryLight}, #1a8aab)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Phone size={24} color={brand.accent} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: brand.textPrimary }}>Self KYC</div>
                  <div style={{ fontSize: 12, color: brand.textSecondary, marginTop: 3, lineHeight: 1.4 }}>
                    Send link — wait for patron to complete verification
                  </div>
                </div>
                <ChevronRight size={20} color={brand.textMuted} />
              </div>
            </div>

            <div onClick={handleNswDigital} style={{ ...styles.card, cursor: "pointer", border: `1.5px solid ${brand.primary}20`, padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: `linear-gradient(135deg, #1a5f7a, #0d6d8a)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CreditCard size={24} color={brand.accent} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: brand.textPrimary }}>NSW Digital Licence</div>
                  <div style={{ fontSize: 12, color: brand.textSecondary, marginTop: 3, lineHeight: 1.4 }}>
                    Patron scans venue QR with Service NSW app — pre-verified, no DVS
                  </div>
                </div>
                <ChevronRight size={20} color={brand.textMuted} />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Self KYC — patron mobile (slip already captured at step 0) */}
        {step === 2 && kycMode === "self" && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Self verification</div>
              <div style={{ fontSize: 12, color: brand.textSecondary, marginBottom: 12 }}>Send a secure link for the patron to complete verification on their device.</div>
              <label style={styles.label}>Patron mobile number</label>
              <input
                style={styles.input}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+61 412 345 678"
                value={selfKycMobile}
                onChange={(e) => setSelfKycMobile(e.target.value)}
              />
              <div style={{ fontSize: 11, color: brand.textMuted, marginTop: 8 }}>
                Enter any number to simulate SMS — include country code (e.g. +61) or local 04…
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={styles.btn("secondary")} onClick={() => { setStep(1); setKycMode(null); }}>Back</button>
              <button style={styles.btn("primary")} onClick={handleSendSelfKycLink}>
                Send verification link
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: NSW Digital Licence — Show venue QR (after mode selection) */}
        {step === 2 && kycMode === "nsw_digital" && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={styles.card}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <Info size={18} color={brand.primary} />
                <span style={{ fontSize: 13, fontWeight: 700, color: brand.primary }}>NSW Digital Driver Licence</span>
              </div>
              <div style={{ fontSize: 12, color: brand.textSecondary, lineHeight: 1.5, marginBottom: 16 }}>
                Ask patron to open the Service NSW app, tap "Share licence" and scan this venue QR code. Licence details will be shared directly — no DVS check needed (pre-verified by Service NSW).
              </div>
              {!nswDigitalDataReceived ? (
                <>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                    <div style={{ width: 200, height: 200, background: "#fff", display: "grid", gridTemplateColumns: "repeat(25, 1fr)", gridTemplateRows: "repeat(25, 1fr)", gap: 0, padding: 16, borderRadius: 8, border: `2px solid ${brand.border}` }}>
                      {Array.from({ length: 625 }).map((_, i) => {
                        const r = Math.floor(i / 25), c = i % 25;
                        const isCorner = (r < 8 && c < 8) || (r < 8 && c >= 17) || (r >= 17 && c < 8);
                        const isModule = isCorner || (r % 2 === 0 && c % 2 === 0) || (r === 12 || c === 12);
                        return <div key={i} style={{ background: isModule ? "#000" : "#fff", minWidth: 2, minHeight: 2 }} />;
                      })}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: brand.textMuted, textAlign: "center", marginBottom: 16 }}>Venue QR Code — WEG-TAM-001</div>
                  <button style={styles.btn("primary")} onClick={() => setTimeout(() => setNswDigitalDataReceived(true), 1500)}>
                    Simulate: Patron scanned & shared
                  </button>
                </>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <CheckCircle size={18} color={brand.success} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: brand.success }}>Licence data received</span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: brand.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" }}>From NSW Digital Licence</div>
                  {[
                    { label: "Full Name", value: "David Robert MORRISON" },
                    { label: "Date of Birth", value: "14/03/1985" },
                    { label: "Address", value: "42 Peel St, Tamworth NSW 2340" },
                    { label: "Licence No.", value: "DL 12345678" },
                    { label: "Expiry", value: "14/03/2028" },
                  ].map((f, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < 4 ? `1px solid ${brand.borderLight}` : "none" }}>
                      <span style={{ fontSize: 12, color: brand.textMuted }}>{f.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: brand.textPrimary, textAlign: "right" }}>{f.value}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 12, padding: 10, background: brand.successBg, borderRadius: 8, border: `1px solid ${brand.success}30` }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: brand.success }}>✓ Pre-verified by Service NSW — no separate DVS check required</div>
                  </div>
                </>
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={styles.btn("secondary")} onClick={() => nswDigitalDataReceived ? setNswDigitalDataReceived(false) : setStep(1)}>Back</button>
              {nswDigitalDataReceived && (
                <button style={styles.btn("primary")} onClick={() => setStep(5)}>
                  Continue to payment
                  <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Scan Document — Front & optional Back (Assisted only) */}
        {step === 2 && kycMode === "assisted" && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            {docFrontCaptured && docBackCaptured === null && showBackScanUI ? (
              /* Scan back of card — full camera UI */
              <>
                <div style={{ ...styles.card, padding: 0, overflow: "hidden" }}>
                  <div style={{ padding: 12, background: brand.primaryMuted, borderBottom: `1px solid ${brand.border}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: brand.primary }}>Back of card</div>
                    <div style={{ fontSize: 11, color: brand.textSecondary, marginTop: 2 }}>Some states have info on the back — position and scan</div>
                  </div>
                  <div style={{ height: 260, background: "#111", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative" }}>
                    {scanning ? (
                      <>
                        <Loader size={36} color={brand.accent} style={{ animation: "spin 1.5s linear infinite" }} />
                        <div style={{ color: brand.accent, fontSize: 13, fontWeight: 600, marginTop: 12 }}>Scanning back…</div>
                        <div style={styles.progressBar}>
                          <div style={{ ...styles.progressFill(70), width: "70%" }} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ width: 240, height: 150, border: `2.5px dashed rgba(253,227,0,0.6)`, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, textAlign: "center", padding: 20 }}>Position back of ID within frame</div>
                        </div>
                        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "12px 16px", background: "linear-gradient(transparent, rgba(0,0,0,0.7))" }}>
                          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, textAlign: "center" }}>Align document, then tap Scan</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button style={styles.btn("secondary")} onClick={() => setShowBackScanUI(false)}>Back</button>
                  <button style={styles.btn("primary")} onClick={() => handleScan("back")} disabled={scanning}>
                    <Camera size={16} />
                    {scanning ? "Scanning…" : "Scan back"}
                  </button>
                </div>
              </>
            ) : docFrontCaptured && docBackCaptured === null ? (
              /* Front done — choose to scan back or skip */
              <>
                <div style={styles.card}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <CheckCircle size={18} color={brand.success} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: brand.success }}>Front of card captured</span>
                  </div>
                  <div style={{ fontSize: 12, color: brand.textSecondary, lineHeight: 1.5 }}>
                    Some Australian IDs (e.g. driver's licence) have information on the back. Capture the back if required, or skip if not needed.
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button style={styles.btn("primary")} onClick={() => setShowBackScanUI(true)}>
                    <Camera size={16} />
                    Scan back of card
                  </button>
                  <button style={styles.btn("secondary")} onClick={handleSkipBack}>
                    Skip — back not required
                  </button>
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                  <button style={styles.btn("ghost")} onClick={() => { setDocFrontCaptured(false); setDocBackCaptured(null); }}>Re-scan front</button>
                </div>
              </>
            ) : (
              /* Scan front (or back if they chose to scan) */
              <>
                <div style={{ ...styles.card, padding: 0, overflow: "hidden" }}>
                  <div style={{ padding: 12, background: brand.primaryMuted, borderBottom: `1px solid ${brand.border}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: brand.primary }}>
                      {docFrontCaptured ? "Back of card" : "Front of card"}
                    </div>
                    <div style={{ fontSize: 11, color: brand.textSecondary, marginTop: 2 }}>
                      {docFrontCaptured ? "Some states have info on the back" : "Position ID within frame"}
                    </div>
                  </div>
                  <div style={{ height: 260, background: "#111", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative" }}>
                    {scanning ? (
                      <>
                        <Loader size={36} color={brand.accent} style={{ animation: "spin 1.5s linear infinite" }} />
                        <div style={{ color: brand.accent, fontSize: 13, fontWeight: 600, marginTop: 12 }}>Scanning…</div>
                        <div style={styles.progressBar}>
                          <div style={{ ...styles.progressFill(70), width: "70%" }} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ width: 240, height: 150, border: `2.5px dashed rgba(253,227,0,0.6)`, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                          <div style={{ position: "absolute", top: -2, left: -2, width: 20, height: 20, borderTop: `3px solid ${brand.accent}`, borderLeft: `3px solid ${brand.accent}`, borderRadius: "4px 0 0 0" }} />
                          <div style={{ position: "absolute", top: -2, right: -2, width: 20, height: 20, borderTop: `3px solid ${brand.accent}`, borderRight: `3px solid ${brand.accent}`, borderRadius: "0 4px 0 0" }} />
                          <div style={{ position: "absolute", bottom: -2, left: -2, width: 20, height: 20, borderBottom: `3px solid ${brand.accent}`, borderLeft: `3px solid ${brand.accent}`, borderRadius: "0 0 0 4px" }} />
                          <div style={{ position: "absolute", bottom: -2, right: -2, width: 20, height: 20, borderBottom: `3px solid ${brand.accent}`, borderRight: `3px solid ${brand.accent}`, borderRadius: "0 0 4px 0" }} />
                          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, textAlign: "center", padding: 20 }}>Position ID within frame</div>
                        </div>
                        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "12px 16px", background: "linear-gradient(transparent, rgba(0,0,0,0.7))" }}>
                          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, textAlign: "center" }}>Align document, then tap Scan</div>
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{ padding: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: brand.textPrimary, marginBottom: 4 }}>Accepted Documents</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {["Driver's Licence", "Passport", "Proof of Age"].map((d) => (
                        <span key={d} style={{ ...styles.badge("primary"), fontSize: 10 }}>{d}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button style={styles.btn("secondary")} onClick={() => docFrontCaptured ? (setDocFrontCaptured(false), setDocBackCaptured(null)) : setStep(1)}>Back</button>
                  <button style={styles.btn("primary")} onClick={() => handleScan("front")} disabled={scanning}>
                    <Camera size={16} />
                    {scanning ? "Scanning…" : docFrontCaptured ? "Scan back" : "Scan front"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 3: OCR Preview */}
        {step === 3 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={styles.card}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <CheckCircle size={18} color={brand.success} />
                <span style={{ fontSize: 14, fontWeight: 700, color: brand.success }}>Document Scanned</span>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: brand.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>OCR Extracted Data</div>
              {[
                { label: "Full Name", value: "David Robert MORRISON" },
                { label: "Date of Birth", value: "14/03/1985" },
                { label: "Address", value: "42 Peel St, Tamworth NSW 2340" },
                { label: "Document No.", value: "DL 12345678" },
                { label: "Document Type", value: "NSW Driver's Licence" },
                { label: "Expiry Date", value: "14/03/2028" },
              ].map((f, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: i < 5 ? `1px solid ${brand.borderLight}` : "none" }}>
                  <span style={{ fontSize: 12, color: brand.textMuted }}>{f.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: brand.textPrimary, textAlign: "right" }}>{f.value}</span>
                </div>
              ))}
            </div>
            <div style={{ ...styles.card, background: brand.warningBg, border: `1px solid ${brand.warning}30` }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <Eye size={16} color={brand.warning} style={{ marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 12, color: brand.warning, fontWeight: 600, lineHeight: 1.5 }}>
                  Verify patron matches document photo before proceeding. Confirm extracted data is correct.
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={styles.btn("secondary")} onClick={() => setStep(2)}>Re-scan</button>
              <button style={styles.btn("primary")} onClick={() => setShowDvsConsent(true)}>
                Approve & Run DVS
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: DVS Verification */}
        {step === 4 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={{ ...styles.card, display: "flex", flexDirection: "column", alignItems: "center", padding: 40 }}>
              <Loader size={40} color={brand.primary} style={{ animation: "spin 1.5s linear infinite" }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: brand.textPrimary, marginTop: 16 }}>Verifying with DVS</div>
              <div style={{ fontSize: 12, color: brand.textMuted, marginTop: 6, textAlign: "center" }}>
                Document Verification Service is checking identity credentials…
              </div>
              <div style={{ ...styles.progressBar, width: "80%", marginTop: 20 }}>
                <div style={styles.progressFill(60)} />
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Complete */}
        {step === 5 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            {kycMode === "self" && selfKycPhase === "sent" ? (
              /* Waiting for patron to complete self KYC */
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 0" }}>
                <Loader size={48} color={brand.primary} style={{ animation: "spin 1.5s linear infinite", marginBottom: 20 }} />
                <div style={{ fontSize: 18, fontWeight: 700, color: brand.textPrimary, textAlign: "center" }}>Waiting for patron</div>
                <div style={{ fontSize: 13, color: brand.textSecondary, marginTop: 8, textAlign: "center", lineHeight: 1.5 }}>
                  SMS link sent to <strong style={{ color: brand.textPrimary }}>{String(selfKycMobile || "").trim() || "—"}</strong>. Device will receive confirmation when patron completes verification.
                </div>
                <div style={{ marginTop: 24, fontSize: 12, color: brand.textMuted }}>Checking for completion…</div>
                <button style={{ ...styles.btn("ghost"), marginTop: 24 }} onClick={() => { setSelfKycPhase(null); setStep(2); }}>Cancel</button>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 0 24px" }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: brand.successBg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, boxShadow: `0 0 0 8px ${brand.success}15` }}>
                    <CheckCircle size={36} color={brand.success} />
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: brand.textPrimary }}>
                    {kycMode === "self" ? "Verification complete" : kycMode === "nsw_digital" ? "Identity verified" : "Submitted to Backend"}
                  </div>
                  <div style={{ fontSize: 13, color: brand.textSecondary, marginTop: 6, textAlign: "center" }}>
                    {kycMode === "self" ? "Patron completed verification. Payout can proceed." : kycMode === "nsw_digital" ? "NSW Digital Licence verified. Payout can proceed." : "Identity verified. Payout can proceed."}
                  </div>
                </div>

            {kycMode === "assisted" && (
              <div style={styles.card}>
                <div style={{ fontSize: 12, fontWeight: 700, color: brand.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>Verification Summary</div>
                {[
                  { label: "Patron", value: `${slip.playerFirst} ${slip.playerLast}`.trim(), status: null },
                  { label: "Payout Amount", value: new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(amountNum), status: null },
                  { label: "Identity", value: "Verified", status: "success" },
                  { label: "Document", value: "Verified", status: "success" },
                ].map((r, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < 3 ? `1px solid ${brand.borderLight}` : "none" }}>
                    <span style={{ fontSize: 12, color: brand.textMuted }}>{r.label}</span>
                    {r.status ? <span style={styles.badge("success")}>✓ {r.value}</span> : <span style={{ fontSize: 13, fontWeight: 600, color: brand.textPrimary }}>{r.value}</span>}
                  </div>
                ))}
              </div>
            )}

            {kycMode === "self" && selfKycPhase === "complete" && (
              <div style={{ ...styles.card, background: brand.successBg, border: `1px solid ${brand.success}30` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <CheckCircle size={18} color={brand.success} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: brand.success }}>Patron completed verification</div>
                    <div style={{ fontSize: 11, color: brand.textSecondary, marginTop: 2 }}>Data received from patron's device. Identity verified.</div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button style={styles.btn("secondary")} onClick={resetJourney}>New Payout</button>
              <button style={styles.btn("primary")} onClick={() => setStep(6)}>
                <CheckCircle size={16} />
                Proceed to Pay
              </button>
            </div>
              </>
            )}
          </div>
        )}

        {/* Step 6: Payment Method & Signature (driven by venue payout policy tier) */}
        {step === 6 && (
          <div style={{ animation: "fadeIn 0.3s ease", position: "relative", minHeight: signingFor ? 420 : undefined }}>
            {payTier && (
              <div style={{ ...styles.card, background: brand.primaryMuted, border: `1px solid ${brand.primary}25`, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: brand.primary }}>Payout tier · {payTier.tierId}</div>
                <div style={{ fontSize: 12, color: brand.textSecondary, marginTop: 4 }}>
                  {String(cashDispensed).replaceAll("_", " ")} · Issuer: {humanizeRole(payTier.policy.issuerRoleId)}
                </div>
              </div>
            )}
            {payTier?.policy?.pinEntryByRoleId && (
              <div style={{ ...styles.card, background: brand.warningBg, border: `1px solid ${brand.warning}35`, marginBottom: 12, fontSize: 12, color: brand.textSecondary }}>
                <strong style={{ color: brand.primary }}>PIN required</strong> — {humanizeRole(payTier.policy.pinEntryByRoleId)} must enter PIN to authorise (integrate with terminal).
              </div>
            )}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Payment Method</div>
              <div style={{ fontSize: 11, color: brand.textMuted, marginBottom: 8 }}>Allowed methods for this tier</div>
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {(cashDispensed === "CRT_OR_CASH" ? ["Cash", "EFT", "Cheque"] : cashDispensed === "CASH_ONLY" ? ["Cash"] : ["EFT", "Cheque"]).map((m) => (
                  <div key={m} onClick={() => setPaymentMethod(m)} style={{ flex: "1 1 30%", minWidth: 90, padding: 12, borderRadius: 10, border: `1.5px solid ${paymentMethod === m ? brand.primary : brand.border}`, background: paymentMethod === m ? brand.primaryMuted : "#fff", cursor: "pointer", textAlign: "center", fontSize: 13, fontWeight: 700, color: paymentMethod === m ? brand.primary : brand.textSecondary }}>
                    {m}
                  </div>
                ))}
              </div>
            </div>
            {signerSlots.length > 0 && (
              <div style={styles.card}>
                <div style={styles.cardTitle}>Signatures</div>
                <div style={{ fontSize: 12, color: brand.textMuted, marginBottom: 12 }}>
                  {signerSlots.length === 0
                    ? "No signatures required for this tier."
                    : `Capture a signature for each role (${signerSlots.map((s) => s.label).join(", ")}) — tier ${payTier?.tierId || "—"}.`}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: signerSlots.length === 1 ? "1fr" : "repeat(auto-fill, minmax(140px, 1fr))",
                    gap: 12,
                  }}
                >
                  {signerSlots.map((slot) => {
                    const img = signatureImages[slot.key];
                    return (
                      <div
                        key={slot.key}
                        onClick={() => setSigningFor({ key: slot.key, label: slot.label })}
                        style={{
                          padding: 12,
                          borderRadius: 10,
                          border: `1.5px solid ${img ? brand.success : brand.border}`,
                          background: img ? brand.successBg : "#fff",
                          cursor: "pointer",
                          textAlign: "center",
                          minHeight: 88,
                        }}
                      >
                        <div style={{ fontSize: 11, fontWeight: 600, color: brand.textMuted, marginBottom: 6 }}>{slot.label}</div>
                        {img ? (
                          <img src={img} alt="" style={{ width: "100%", height: 50, objectFit: "contain", borderRadius: 4 }} />
                        ) : (
                          <div style={{ fontSize: 12, color: brand.textMuted, paddingTop: 12 }}>Tap to sign</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {signingFor && (
              <SignaturePad
                title={`${signingFor.label} — signature`}
                onDone={(img) => {
                  setSignatureImages((prev) => ({ ...prev, [signingFor.key]: img }));
                  setSigningFor(null);
                }}
                onCancel={() => setSigningFor(null)}
              />
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button style={styles.btn("secondary")} onClick={() => (kycSkipped ? setStep(0) : setStep(5))}>Back</button>
              <button
                style={styles.btn("primary")}
                disabled={
                  signerSlots.length > 0 &&
                  !signerSlots.every((s) => signatureImages[s.key])
                }
                onClick={resetJourney}
              >
                <CheckCircle size={16} />
                Complete Payout
              </button>
            </div>
          </div>
        )}
      </div>
      {showDvsConsent && (
        <DvsPrivacyConsentModal
          onAgree={() => { setShowDvsConsent(false); handleDVS(); }}
          onDecline={() => setShowDvsConsent(false)}
        />
      )}
    </div>
  );
};

// ─── MJ2: INCIDENT REPORT JOURNEY ──────────────────────────────
const MJ2Screen = () => {
  const [step, setStep] = useState(0); // 0=form, 1=review, 2=done
  const [desc, setDesc] = useState("");
  const [egm, setEgm] = useState("");
  const [incidentType, setIncidentType] = useState(null);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const incidentTypes = [
    { id: "third_party", label: "Third-party Collection", icon: Users, desc: "Someone collecting on behalf of another" },
    { id: "structuring", label: "Structuring / Smurfing", icon: CreditCard, desc: "Multiple small payouts to avoid threshold" },
    { id: "suspicious", label: "Suspicious Behaviour", icon: Eye, desc: "General suspicious or unusual activity" },
    { id: "other", label: "Other Concern", icon: AlertCircle, desc: "Other reportable activity" },
  ];

  const handleSubmit = () => {
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setStep(2); }, 1500);
  };

  const resetJourney = () => {
    setStep(0); setDesc(""); setEgm(""); setIncidentType(null); setHasPhoto(false); setSubmitting(false);
  };

  return (
    <>
      <Header
        title="Incident Report"
        subtitle={step === 0 ? "Report suspicious activity" : step === 1 ? "Review & submit" : "Submitted"}
        onBack={step === 1 ? () => setStep(0) : null}
      />
      <div style={styles.body}>

        {/* Step 0: Capture Details */}
        {step === 0 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            {/* Incident Type */}
            <div style={{ fontSize: 12, fontWeight: 700, color: brand.textSecondary, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>Incident Category</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18 }}>
              {incidentTypes.map((t) => (
                <div key={t.id} onClick={() => setIncidentType(t.id)} style={{
                  ...styles.card, marginBottom: 0, cursor: "pointer", padding: 14, textAlign: "center",
                  border: incidentType === t.id ? `2px solid ${brand.primary}` : `1.5px solid ${brand.border}`,
                  background: incidentType === t.id ? brand.primaryMuted : "#fff",
                  transition: "all 0.2s",
                }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: incidentType === t.id ? brand.primary : `${brand.primary}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 8px" }}>
                    <t.icon size={18} color={incidentType === t.id ? brand.accent : brand.primary} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: brand.textPrimary }}>{t.label}</div>
                  <div style={{ fontSize: 10, color: brand.textMuted, marginTop: 3, lineHeight: 1.3 }}>{t.desc}</div>
                </div>
              ))}
            </div>

            {/* Location */}
            <div style={styles.card}>
              <label style={styles.label}>Basestation / EGM Location</label>
              <div style={{ position: "relative" }}>
                <MapPin size={16} color={brand.textMuted} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
                <input style={{ ...styles.input, paddingLeft: 36 }} value={egm} onChange={(e) => setEgm(e.target.value)} placeholder="e.g. EGM-042, Zone B, Basestation 3" />
              </div>
            </div>

            {/* Description */}
            <div style={styles.card}>
              <label style={styles.label}>Description of Activity</label>
              <textarea
                style={{ ...styles.input, minHeight: 100, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="Describe what you observed — who, what, where, when. Include any relevant details about patron behaviour or machine activity."
              />
            </div>

            {/* Photo */}
            <div style={styles.card}>
              <label style={styles.label}>Photo Evidence (Optional)</label>
              {!hasPhoto ? (
                <div onClick={() => setHasPhoto(true)} style={{ border: `2px dashed ${brand.border}`, borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer", transition: "border-color 0.2s" }}>
                  <Camera size={28} color={brand.textMuted} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: brand.textMuted }}>Tap to capture photo</span>
                </div>
              ) : (
                <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", background: "#e2e8f0", height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ fontSize: 12, color: brand.textMuted }}>📷 Photo captured</div>
                  <div onClick={() => setHasPhoto(false)} style={{ position: "absolute", top: 8, right: 8, width: 24, height: 24, borderRadius: 12, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                    <X size={14} color="#fff" />
                  </div>
                  <span style={{ position: "absolute", bottom: 8, left: 8, ...styles.badge("success"), fontSize: 10 }}>
                    <CheckCircle size={10} /> Attached
                  </span>
                </div>
              )}
            </div>

            <button
              style={{ ...styles.btn("large"), opacity: (incidentType && desc.length > 0) ? 1 : 0.5 }}
              onClick={() => { if (incidentType && desc.length > 0) setStep(1); }}
            >
              Review Report
              <ArrowRight size={18} />
            </button>
          </div>
        )}

        {/* Step 1: Review */}
        {step === 1 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={styles.card}>
              <div style={{ fontSize: 14, fontWeight: 700, color: brand.textPrimary, marginBottom: 14 }}>Incident Summary</div>
              {[
                { label: "Category", value: incidentTypes.find(t => t.id === incidentType)?.label },
                { label: "Location", value: egm || "Not specified" },
                { label: "Photo", value: hasPhoto ? "1 attached" : "None" },
                { label: "Reported By", value: "Sarah Chen (Floor Manager)" },
                { label: "Timestamp", value: "19 Mar 2026, 9:41 AM" },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < 4 ? `1px solid ${brand.borderLight}` : "none" }}>
                  <span style={{ fontSize: 12, color: brand.textMuted }}>{r.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: brand.textPrimary, textAlign: "right", maxWidth: "60%" }}>{r.value}</span>
                </div>
              ))}
              <div style={{ marginTop: 14, padding: 12, background: brand.surface, borderRadius: 10, border: `1px solid ${brand.borderLight}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: brand.textMuted, marginBottom: 4, textTransform: "uppercase" }}>Description</div>
                <div style={{ fontSize: 13, color: brand.textPrimary, lineHeight: 1.5 }}>{desc}</div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button style={styles.btn("secondary")} onClick={() => setStep(0)}>Edit</button>
              <button style={styles.btn("primary")} onClick={handleSubmit} disabled={submitting}>
                {submitting ? <><Loader size={16} style={{ animation: "spin 1s linear infinite" }} /> Submitting…</> : <><Send size={16} /> Submit Report</>}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Done */}
        {step === 2 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "30px 0 24px" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: brand.successBg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, boxShadow: `0 0 0 8px ${brand.success}15` }}>
                <Send size={32} color={brand.success} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: brand.textPrimary }}>Incident Reported</div>
              <div style={{ fontSize: 13, color: brand.textSecondary, marginTop: 6, textAlign: "center", lineHeight: 1.5 }}>
                Report submitted. Reference number below.
              </div>
            </div>

            <div style={styles.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: brand.textMuted }}>Reference</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: brand.primary, fontFamily: "monospace" }}>INC-2026-0147</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: `1px solid ${brand.borderLight}` }}>
                <span style={{ fontSize: 12, color: brand.textMuted }}>Status</span>
                <span style={styles.badge("warning")}>
                  <Clock size={10} /> Pending review
                </span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button style={styles.btn("secondary")} onClick={resetJourney}>New Report</button>
              <button style={styles.btn("primary")}>
                <Home size={16} />
                Back to Home
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ─── ALERTS SCREEN ───────────────────────────────────────────────
const AlertsScreen = () => {
  const [alerts, setAlerts] = useState([
    { id: "ALT-0147", memberName: "David Morrison", memberId: "M3022", documentType: "Source of Funds", coInstructions: "Bank statement or payslip.", severity: "high", time: "9:41 AM", status: "pending", feedback: null, expectedBy: null, hasDocument: false },
    { id: "ALT-0146", memberName: "Mark Wilson", memberId: "M2841", documentType: "Source of Funds", coInstructions: "Bank statement or payslip.", severity: "critical", time: "8:15 AM", status: "requested", feedback: "requested", expectedBy: "Tomorrow", hasDocument: false },
    { id: "ALT-0145", memberName: "Lisa Chen", memberId: "M3102", documentType: "Source of Funds", coInstructions: "Bank statement or payslip.", severity: "medium", time: "Yesterday", status: "refused", feedback: "refused", expectedBy: null, hasDocument: false },
  ]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [expectedBy, setExpectedBy] = useState(null);

  const updateAlert = (id, updates) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, ...updates } : a)));
  };

  const handleSaveFeedback = () => {
    if (!selectedAlert) return;
    updateAlert(selectedAlert.id, { status: feedback === "refused" ? "refused" : feedback === "requested" ? "requested" : "pending", feedback, expectedBy });
    setSelectedAlert(null);
    setFeedback(null);
    setExpectedBy(null);
  };

  const handleUpload = () => {
    if (!selectedAlert) return;
    setTimeout(() => updateAlert(selectedAlert.id, { hasDocument: true, status: "uploaded" }), 800);
  };

  useEffect(() => {
    if (selectedAlert) {
      const a = alerts.find((x) => x.id === selectedAlert.id) || selectedAlert;
      setFeedback(a.feedback);
      setExpectedBy(a.expectedBy);
    }
  }, [selectedAlert?.id]);

  const pendingCount = alerts.filter((a) => a.status === "pending" || (a.status === "requested" && !a.hasDocument)).length;

  if (selectedAlert) {
    const alert = alerts.find((a) => a.id === selectedAlert.id) || selectedAlert;
    return (
      <>
        <Header title="Alert" subtitle={alert.id} onBack={() => { setSelectedAlert(null); setFeedback(null); setExpectedBy(null); }} />
        <div style={styles.body}>
          <div style={styles.card}>
            <div style={{ fontSize: 12, fontWeight: 700, color: brand.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>Member</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: brand.textPrimary }}>{alert.memberName}</div>
            <div style={{ fontSize: 12, color: brand.textMuted, marginTop: 2 }}>{alert.memberId}</div>
          </div>
          <div style={styles.card}>
            <div style={{ fontSize: 12, fontWeight: 700, color: brand.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>Document required</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: brand.textPrimary }}>{alert.documentType}</div>
          </div>
          <div style={styles.card}>
            <div style={{ fontSize: 12, fontWeight: 700, color: brand.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>Instructions</div>
            <div style={{ fontSize: 13, color: brand.textPrimary, lineHeight: 1.5 }}>{alert.coInstructions}</div>
          </div>

          <div style={{ ...styles.card, background: brand.primaryMuted, border: `1px solid ${brand.primary}15`, padding: 12 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <Info size={16} color={brand.primary} style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: brand.primary, marginBottom: 6 }}>How to approach</div>
                <div style={{ fontSize: 12, color: brand.textSecondary, lineHeight: 1.55 }}>
                  <strong>Check availability:</strong> If you see them on the floor, approach when they’re free — a casual, friendly check-in works best.<br /><br />
                  <strong>What to say:</strong> “We’re updating our records and need a quick document to complete your file — bank statement or payslip is fine. It’s routine for all members.”<br /><br />
                  <strong>Keep it natural:</strong> Frame it as a standard admin request. Do not mention any specific transaction or reason.
                </div>
              </div>
            </div>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: brand.textSecondary, marginBottom: 8 }}>Feedback</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div onClick={() => setFeedback("requested")} style={{ ...styles.card, cursor: "pointer", border: `1.5px solid ${feedback === "requested" ? brand.primary : brand.border}`, background: feedback === "requested" ? brand.primaryMuted : "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CheckCircle size={18} color={feedback === "requested" ? brand.primary : brand.textMuted} />
                <span style={{ fontSize: 13, fontWeight: 600, color: brand.textPrimary }}>Requested patron</span>
              </div>
              {feedback === "requested" && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: brand.textMuted, marginBottom: 4 }}>Expected by</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {["Today", "Tomorrow", "In 3 days", "Next week"].map((d) => (
                      <span key={d} onClick={(e) => { e.stopPropagation(); setExpectedBy(d); }} style={{ padding: "6px 12px", borderRadius: 8, background: expectedBy === d ? brand.primary : brand.border, color: expectedBy === d ? "#fff" : brand.textSecondary, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{d}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div onClick={() => setFeedback("refused")} style={{ ...styles.card, cursor: "pointer", border: `1.5px solid ${feedback === "refused" ? brand.error : brand.border}`, background: feedback === "refused" ? brand.errorBg : "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <XCircle size={18} color={feedback === "refused" ? brand.error : brand.textMuted} />
                <span style={{ fontSize: 13, fontWeight: 600, color: brand.textPrimary }}>Refused by patron</span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <button style={styles.btn("primary")} onClick={handleUpload} disabled={alert.hasDocument}>
              <Camera size={16} />
              {alert.hasDocument ? "Document uploaded ✓" : "Upload document / Take photo"}
            </button>
          </div>

          <button style={{ ...styles.btn("ghost"), marginTop: 12, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} onClick={() => alert("Message CO — conversation flow TBD")}>
            <MessageCircle size={16} />
            Message for more details
          </button>

          {(feedback || alert.hasDocument) && (
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button style={styles.btn("secondary")} onClick={() => setSelectedAlert(null)}>Cancel</button>
              <button style={styles.btn("primary")} onClick={handleSaveFeedback}>
                <CheckCircle size={16} />
                Save
              </button>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <Header title="Alerts" subtitle={`${pendingCount} pending`} />
      <div style={styles.body}>
        {alerts.map((a) => (
          <div key={a.id} onClick={() => setSelectedAlert(a)} style={{ ...styles.card, cursor: "pointer", borderLeft: `4px solid ${a.severity === "critical" ? brand.error : a.severity === "high" ? "#ea580c" : brand.warning}`, transition: "all 0.2s" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                  <span style={styles.badge(a.status === "pending" ? (a.severity === "critical" ? "error" : "warning") : a.status === "uploaded" ? "success" : a.status === "refused" ? "error" : "primary")}>
                    {a.status === "pending" ? "PENDING" : a.status === "requested" ? `Requested (${a.expectedBy || "—"})` : a.status === "refused" ? "Refused" : "Uploaded"}
                  </span>
                  <span style={{ fontSize: 10, color: brand.textMuted, fontFamily: "monospace" }}>{a.id}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: brand.textPrimary }}>{a.documentType} — {a.memberName}</div>
                <div style={{ fontSize: 12, color: brand.textMuted, marginTop: 3 }}>{a.coInstructions.slice(0, 50)}…</div>
              </div>
              <span style={{ fontSize: 10, color: brand.textMuted, flexShrink: 0 }}>{a.time}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

// ─── MAIN APP ────────────────────────────────────────────────────
export default function PaySafeMobile() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("home");

  return (
    <div style={{ width: "min(390px, 100vw)", height: "min(844px, 100vh)", overflow: "hidden", position: "relative", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", boxShadow: "0 4px 24px rgba(0,0,0,0.15)", borderRadius: 36, background: brand.surface }}>
      <div style={{ ...styles.phoneInner, width: "100%", height: "100%", borderRadius: 36 }}>
          {/* Notch */}
          <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 120, height: 28, background: brand.primary, borderRadius: "0 0 20px 20px", zIndex: 20 }}>
            <div style={{ width: 10, height: 10, borderRadius: 5, background: "rgba(0,0,0,0.2)", position: "absolute", right: 28, top: 8 }} />
          </div>

          <StatusBar />
          {!user && <LoginScreen onLogin={setUser} />}
          {user && activeTab === "home" && <HomeScreen onNav={setActiveTab} />}
          {user && activeTab === "payout" && <MJ1Screen />}
          {user && activeTab === "incident" && <MJ2Screen />}
          {user && activeTab === "alerts" && <AlertsScreen />}
          {user && <BottomTabBar activeTab={activeTab} onTabChange={setActiveTab} />}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 0; }
        input:focus, textarea:focus { border-color: ${brand.primary} !important; box-shadow: 0 0 0 3px ${brand.primary}20; }
      `}</style>
    </div>
  );
}
